import { colors } from '@factorialco/deprecated-design-system'
import { DatumValue } from '@nivo/core'
import { Point, Serie } from '@nivo/line'
import { FinancialWorkspaceValueObjectsPeriodEnum } from 'generated/resources/graphql'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import {
  ChartData,
  PlotData,
} from 'modules/finance/components/FinancialWorkspace/components/Table/types'
import { formatYAxisWithLargeNumbers } from 'modules/project_management/lib/formatChartComponents'

const CENT = 100
const BASE_CURRENCY = 'EUR'

const enum Period {
  hourly = 'hourly',
  daily = 'daily',
  weekly = 'weekly',
  monthly = 'monthly',
  quarterly = 'quarterly',
  yearly = 'yearly',
}

const EMPTY_CHART_DATA: ChartData[] = [
  {
    serieId: 'empty',
    pointColor: colors.viridian1000,
    hoveredPointColor: colors.grey1000,
    data: [
      { x: 'Start', y: null, title: 'Start' },
      { x: 'End', y: 999999, title: 'End' },
    ],
  },
]

const highestPlotDataPoint = (plotData?: PlotData[]) => {
  const data = plotData || []
  return data.reduce((max, obj) => ((obj.y || 0) > (max?.y || 0) ? obj : max), data[0])
}

const parsePlotData = (plotData?: PlotData[]): PlotData[] => {
  const data = (plotData || []).map((obj) => {
    return { ...obj, y: obj.y || null }
  })

  return data
}

const isPlotDataEmpty = (parsedPlotData: PlotData[]) => {
  return !parsedPlotData.find(({ y }) => y !== null)
}

// This will find all non null points in the series that have null points between them return them as
// smaller series with the null points filled with values such that it makes a straight line in the chart
// For instance lets say the month of april has a value of 4000 and following that is july with a value of 1000 in the series
// We will get a sub series starting with april as 4000, may as 3000, june as 2000 and july as 1000
// This way we can plot a dashed straight line from april to july on the chart
const findNullSeries = (plotData: PlotData[]) => {
  const points = plotData || []
  let nullSeries: PlotData[] = []
  let firstNonNullPoint: PlotData
  const nullData: PlotData[][] = []

  points.forEach((point) => {
    if (point.y) {
      if (nullSeries.length) {
        nullSeries.unshift(firstNonNullPoint)
        nullSeries.push({ ...point })
        nullData.push(nullSeries)
        nullSeries = []
        firstNonNullPoint = point
      } else {
        firstNonNullPoint = point
      }
    } else {
      if (firstNonNullPoint) {
        nullSeries.push({ ...point, title: '' })
      }
    }
  })

  return nullData.map((series) => {
    const seriesLength = series.length
    const ySeries = interpolateLinearWithEndpoints(
      Number(series[0]?.y),
      Number(series[seriesLength - 1]?.y),
      seriesLength - 2
    )

    ySeries.forEach((y, idx) => {
      const data = series[idx]
      if (data) data.y = y
    })

    const arr: PlotData[] = []

    points.forEach((obj) => {
      const nullObj = series.find(({ x }) => x === obj.x)
      if (nullObj) {
        arr.push(nullObj)
      } else {
        arr.push({ y: null, x: obj.x, title: '' })
      }
    })

    return arr
  })
}

const generatePlotDataSeries = (series: Serie[], id: string, parsedPlotData: PlotData[]) => {
  const copy = [...parsedPlotData]
  series.push({ data: copy, id })
  const nullSeries = findNullSeries(copy)

  // Add start and end points to the chart
  // These are not real data points, just visual representations of the start and end of the chart
  // Also when the graph is empty these points are used to add dummy data so that there will be labels on the left y-axis line
  const noData = isPlotDataEmpty(copy)
  if (copy.length) {
    copy.unshift({ x: 'Start', y: null, title: 'Start' })
    copy.push({ x: 'End', y: noData ? 999999 : null, title: 'End' })
  }

  nullSeries.forEach((nullData, idx) => {
    nullData.unshift({ x: 'Start', y: null, title: 'Start' })
    nullData.push({ x: 'End', y: noData ? 999999 : null, title: 'End' })
    series.unshift({ data: nullData, id: `${id}-${NULL_SERIES_PREFIX}-${idx}` })
  })
}

const interpolateLinearWithEndpoints = (a: number, b: number, gapCount: number) => {
  const result = [a]

  for (let i = 1; i <= gapCount; i++) {
    const interpolatedValue = a + i * ((b - a) / (gapCount + 1))
    result.push(interpolatedValue)
  }

  result.push(b)

  return result
}

const extractCurrencySymbol = (amount: string): string => {
  const match = amount.match(/^\D+/) // Match all non-digit characters at the start of the string
  return match ? match[0] : '' // Return the matched currency symbol or empty string if not found
}
export const NULL_SERIES_PREFIX = 'null-series'

export const highestDataPoint = (chartData?: ChartData[]) => {
  const plotData: PlotData[] = []
  chartData?.forEach((obj) => {
    obj.data.forEach((data) => plotData.push({ ...data }))
  })
  return highestPlotDataPoint(plotData)
}

export const parseChartData = (chartData?: ChartData[]): ChartData[] => {
  const data = (chartData?.length ? chartData : EMPTY_CHART_DATA).map((obj) => {
    return { ...obj, data: parsePlotData(obj.data) }
  })

  return data
}

export const isEmpty = (parsedChartData: ChartData[]) => {
  return !parsedChartData.find(({ data }) => data.find(({ y }) => y !== null))
}

export const generateSeries = (parsedChartData: ChartData[]) => {
  const series: Serie[] = []

  parsedChartData.forEach((obj) => generatePlotDataSeries(series, obj.serieId, obj.data))

  return series
}

export const isStartOrEndPoint = (point: Point) => {
  return point.data.x === 'Start' || point.data.x === 'End'
}

export const findHoveredPoints = (point: Point | null, series: Serie[]) => {
  const points: {
    serieId: string | number
    y: DatumValue | null | undefined
    x: DatumValue | null | undefined
  }[] = []
  if (!point) return points

  series.forEach((serie) => {
    if (!`${serie.id}`.includes(NULL_SERIES_PREFIX)) {
      const seriePoint = serie.data.find((data) => data.x && data.y && data.x === point.data.x)
      if (seriePoint)
        points.push({
          x: seriePoint.x,
          y: seriePoint.y,
          serieId: serie.id,
        })
    }
  })

  return points
}

export const buildXAxis = (value: string, period?: FinancialWorkspaceValueObjectsPeriodEnum) => {
  switch (period) {
    case Period.yearly:
      return value.split(' ')[0]
    case Period.monthly:
      return `${value.slice(0, 4)}`.toUpperCase()
    case Period.quarterly:
      return value.split(' - ')[0]?.toUpperCase()
    case Period.weekly:
      return value.split(',')[0]
    case Period.daily:
      return value.split(' ')[1]?.replace(',', '')
    case Period.hourly:
      return value.split(',')[0]
    default:
      return value
  }
}

export const theme = {
  grid: { line: { strokeWidth: 0.5 } },
  axis: { ticks: { text: { fill: colors.grey800, fontSize: 12 } } },
}

export interface CentToFormattedAmount {
  amount: string
  formatted: boolean
  formattedAmount: string
}

export const centToFormattedAmount = (
  value: number,
  accessLocale: string,
  currency?: string | null
): CentToFormattedAmount => {
  const amount = formattedAmount({
    amountCents: value,
    currency: currency || BASE_CURRENCY,
    locale: accessLocale,
  })

  const currencySymbol = extractCurrencySymbol(amount)
  const shortAmount = formatYAxisWithLargeNumbers(value / CENT, BASE_CURRENCY).split(' ')[0] || '0'

  return {
    amount,
    formatted: true,
    formattedAmount: ` ${currencySymbol} ${shortAmount}`,
  }
}
